/**
 * Environment detection utilities for Flutter vs Web environments
 */

/**
 * Detects if the app is running within a Flutter WebView
 * @returns {boolean} true if running in Flutter, false otherwise
 */
export const isFlutterEnvironment = () => {
  const win = typeof window !== 'undefined' ? window : undefined;
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;

  // Method 1: Check localStorage (your Flutter app sets this)
  try {
    if (localStorage.getItem('environment') === 'flutter' || 
        localStorage.getItem('platform') === 'mobile') {
      return true;
    }
  } catch (e) {
    // Ignore localStorage errors
  }
  
  // Method 2: Check for Flutter-injected window properties
  if (win?.flutter || 
      win?.flutter_inappwebview || 
      win?.FlutterChannel ||
      win?._flutter) {
    return true;
  }
  
  // Method 3: Check user agent (your Flutter app modifies this)
  const userAgent = nav?.userAgent || '';
  if (userAgent.includes('Flutter') || 
      userAgent.includes('FlutterWebView') ||
      userAgent.includes('wv')) {
    return true;
  }
  
  // Method 4: URL-based detection
  if (String(win?.location?.pathname || '').includes('/mobile-signin') ||
      String(win?.location?.search || '').includes('flutter=true') || 
      String(win?.location?.search || '').includes('mobile=true')) {
    return true;
  }
  
  // Method 5: Fallback - mobile WebView detection
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const hasTouch = !!win && 'ontouchstart' in win;
  const noChrome = !win?.chrome;
  const isLikelyWebView = noChrome && (userAgent.includes('Safari') || userAgent.includes('WebKit'));
  
  if (isMobile && hasTouch && isLikelyWebView) {
    return true;
  }
  
  return false;
};

/**
 * Gets the appropriate logout redirect path based on environment
 * @returns {string} The redirect path for logout
 */
export const getLogoutRedirectPath = () => {
  return isFlutterEnvironment() ? '/mobile-signin' : '/';
};

/**
 * Gets the appropriate login redirect path based on environment
 * @returns {string} The redirect path for sign-in
 */
export const getLoginRedirectPath = () => {
  return isFlutterEnvironment() ? '/mobile-signin' : '/new-login';
};

/**
 * Gets environment-specific configuration
 * @returns {object} Configuration object with environment details
 */
export const getEnvironmentConfig = () => {
  const isFlutter = isFlutterEnvironment();
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  
  return {
    isFlutter,
    isWeb: !isFlutter,
    logoutPath: getLogoutRedirectPath(),
    loginPath: getLoginRedirectPath(),
    userAgent: nav?.userAgent || '',
    platform: isFlutter ? 'flutter' : 'web'
  };
};

// Export default for convenience
const environmentUtils = {
  isFlutterEnvironment,
  getLogoutRedirectPath,
  getLoginRedirectPath,
  getEnvironmentConfig
};

export default environmentUtils;
